import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Nav, Tab } from 'react-bootstrap';
import './MyProfile.css';
import { FaLock, FaUser, FaEye, FaEyeSlash } from 'react-icons/fa';
import EmployerNavbar from '../../Pages/Navbar/EmployerNavbar';
import Swal from 'sweetalert2';
import BaseUrl from '../../ApiServices/BaseUrl';

const MyProfile = () => {
  const [key, setKey] = useState('profile');
  const [userId, setUserId] = useState(null);
  const [employerMeta, setEmployerMeta] = useState({ temporary_id: null });
  const [formData, setFormData] = useState({
    name: '',
    role: 'Employer',
    email: '',
    mobile_number: '',
    location: 'Hong Kong',
    language_preference: 'English',
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const locations = [
    { name: 'Hong Kong', flag: '🇭🇰' },
    { name: 'Singapore', flag: '🇸🇬' },
    { name: 'Macau', flag: '🇲🇴' },
    { name: 'Saudi Arabia', flag: '🇸🇦' },
    { name: 'Qatar', flag: '🇶🇦' },
    { name: 'United Arab Emirates', flag: '🇦🇪' },
    { name: 'State of Kuwait', flag: '🇰🇼' },
    { name: 'Malaysia', flag: '🇲🇾' },
    { name: 'Taiwan', flag: '🇹🇼' },
    { name: 'Oman', flag: '🇴🇲' },
  ];

  const languages = [
    'English',
    'Chinese',
    'Arabic',
    'Malay',
    'Tamil',
    'Hindi',
    'Tagalog',
    'Thai',
  ];

  const togglePassword = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && user.id) {
      setUserId(user.id);
      fetchUserData(user.id);
    } else {
      Swal.fire({
        title: 'Error!',
        text: 'No user found. Please login again.',
        icon: 'error',
        confirmButtonText: 'OK',
      }).then(() => {
        window.location.href = '/login';
      });
    }
  }, []);

  const normalizeValue = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value).trim();
    if (!str) return '';
    const lowered = str.toLowerCase();
    if (lowered === 'null' || lowered === 'undefined') return '';
    return str;
  };

  const buildName = (firstName, lastName, fallbackName = '') => {
    const first = normalizeValue(firstName);
    const last = normalizeValue(lastName);
    const combined = [first, last].filter(Boolean).join(' ').trim();
    return combined || normalizeValue(fallbackName);
  };

  const fetchUserData = async (id) => {
    try {
      const [userRes, employerRes] = await Promise.allSettled([
        fetch(`${BaseUrl}/${id}`),
        fetch(`${BaseUrl}/api/employer/user/${id}`)
      ]);

      const userData =
        userRes.status === 'fulfilled' && userRes.value.ok
          ? await userRes.value.json()
          : {};

      const employerData =
        employerRes.status === 'fulfilled' && employerRes.value.ok
          ? await employerRes.value.json()
          : {};

      const resolvedName = buildName(
        userData.first_name,
        userData.last_name,
        employerData.name || employerData.emp_name
      );

      const resolvedEmail =
        normalizeValue(userData.email) ||
        normalizeValue(employerData.email_id);

      const resolvedMobile =
        normalizeValue(userData.mobile_number) ||
        normalizeValue(employerData.phone_number) ||
        normalizeValue(employerData.whatsapp_number);

      const resolvedLocation =
        normalizeValue(userData.location) ||
        normalizeValue(employerData.country) ||
        'Hong Kong';

      const resolvedLanguage =
        normalizeValue(userData.language_preference) || 'English';

      setFormData((prev) => ({
        ...prev,
        name: resolvedName,
        email: resolvedEmail,
        mobile_number: resolvedMobile,
        location: resolvedLocation,
        language_preference: resolvedLanguage,
        oldPassword: normalizeValue(userData.password),
        newPassword: '',
        confirmPassword: '',
      }));

      setEmployerMeta({
        temporary_id: employerData?.temporary_id || null
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to load user data',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

const handleChange = (e) => {
  const { name, value } = e.target;
  
  // Special handling for name field
  if (name === 'name') {
    // Allow only letters, spaces, apostrophes, and hyphens
    const filteredValue = value.replace(/[^A-Za-z\s\'-]/g, '');
    
    // Prevent multiple consecutive spaces
    const finalValue = filteredValue.replace(/\s+/g, ' ');
    
    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));
  } else {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }
};

const handleUpdate = async (updatedPassword = null) => {
  try {
    const trimmedName = normalizeValue(formData.name);
    const nameParts = trimmedName ? trimmedName.split(/\s+/) : [];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ');
    const payload = {
      first_name: firstName,
      last_name: lastName,
      email: formData.email,
      mobile_number: formData.mobile_number,
      location: formData.location,
      language_preference: formData.language_preference,
      password: updatedPassword || formData.oldPassword || '',
    };
    const response = await fetch(`${BaseUrl}/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('Update failed');
    if (employerMeta.temporary_id) {
      await fetch(`${BaseUrl}/api/employer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          temporary_id: employerMeta.temporary_id,
          name: trimmedName,
          emp_name: firstName,
          email_id: formData.email,
          phone_number: formData.mobile_number,
          country: formData.location,
        }),
      });
    }
    Swal.fire({
      title: 'Success!',
      text: 'Profile updated successfully!',
      icon: 'success',
      confirmButtonText: 'OK',
    });
  } catch (error) {
    console.error('Update error:', error);
    Swal.fire({
      title: 'Error!',
      text: 'Failed to update profile',
      icon: 'error',
      confirmButtonText: 'OK',
    });
  }
};
const handlePasswordUpdate = async () => {
  if (!formData.newPassword) {
    Swal.fire({
      title: 'Error!',
      text: 'Please enter a new password.',
      icon: 'error',
      confirmButtonText: 'OK',
    });
    return;
  }

  if (formData.newPassword.length < 8) {
    Swal.fire({
      title: 'Error!',
      text: 'Password must be at least 8 characters long.',
      icon: 'error',
      confirmButtonText: 'OK',
    });
    return;
  }

  if (formData.newPassword !== formData.confirmPassword) {
    Swal.fire({
      title: 'Error!',
      text: 'Passwords do not match!',
      icon: 'error',
      confirmButtonText: 'OK',
    });
    return;
  }

  try {
    const trimmedName = normalizeValue(formData.name);
    const nameParts = trimmedName ? trimmedName.split(/\s+/) : [];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ');
    const payload = {
      first_name: firstName,
      last_name: lastName,
      email: formData.email,
      mobile_number: formData.mobile_number,
      location: formData.location,
      language_preference: formData.language_preference,
      password: formData.newPassword
    };

    const response = await fetch(`${BaseUrl}/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) throw new Error('Password update failed');

    // Show success alert specifically for password change
    Swal.fire({
      title: 'Success!',
      text: 'Password changed successfully!',
      icon: 'success',
      confirmButtonText: 'OK',
    }).then(() => {
      // Clear password fields after successful change
      setFormData(prev => ({
        ...prev,
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
    });

  } catch (error) {
    console.error('Password update error:', error);
    Swal.fire({
      title: 'Error!',
      text: 'Failed to change password',
      icon: 'error',
      confirmButtonText: 'OK',
    });
  }
};

  return (
    <>
      <EmployerNavbar />
      <div style={{ marginTop: '80px' }}>
        <Container className="profile-settings-container mt-4 mb-5">
          <Tab.Container activeKey={key} onSelect={(k) => setKey(k)}>
            <Nav variant="tabs" className="custom-tabs justify-content-center mb-4">
              <Nav.Item>
                <Nav.Link eventKey="profile" style={{ width: '100%' }}>
                  <FaUser /> Profile
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="password" style={{ width: '100%' }}>
                  <FaLock /> Change Password
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              {/* PROFILE TAB */}
              <Tab.Pane eventKey="profile">
                <Form>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.name}
                          name="name"
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Role</Form.Label>
                        <Form.Control type="text" value={formData.role} disabled />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={formData.email}
                          name="email"
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Mobile No</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.mobile_number}
                          name="mobile_number"
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Location</Form.Label>
                        <Form.Select
                          name="location"
                          value={formData.location}
                          onChange={handleChange}
                        >
                          {locations.map((loc, idx) => (
                            <option key={idx} value={loc.name}>
                              {loc.flag} {loc.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Language Preference</Form.Label>
                        <Form.Select
                          name="language_preference"
                          value={formData.language_preference}
                          onChange={handleChange}
                        >
                          {languages.map((lang, idx) => (
                            <option key={idx}>{lang}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="text-center">
                    <Button
                      variant="success"
                      className="update-button-profile px-5"
                      onClick={() => handleUpdate()}
                    >
                      UPDATE
                    </Button>
                  </div>
                </Form>
              </Tab.Pane>

              {/* PASSWORD TAB */}
              <Tab.Pane eventKey="password">
                <Form>
                  <div className="text-center mb-4">
                    <img
                      src="https://cdn-icons-png.flaticon.com/512/3064/3064197.png"
                      width="80"
                      alt="lock"
                    />
                  </div>

                  <Form.Group className="mb-3 position-relative">
                    <Form.Label>Old Password</Form.Label>
                    <Form.Control
                      type={showPasswords.old ? 'text' : 'password'}
                      value={formData.oldPassword}
                      name="oldPassword"
                      onChange={handleChange}
                      placeholder="Old Password"
                      readOnly
                    />
                    <span
                      className="position-absolute"
                      onClick={() => togglePassword('old')}
                      style={{ top: '38px', right: '10px', cursor: 'pointer' }}
                    >
                      {showPasswords.old ? <FaEyeSlash /> : <FaEye />}
                    </span>
                    <small className="text-primary d-block mt-1">
                      You have signed up using Social Login and so no need to add old password
                      but you can set a new password.
                    </small>
                  </Form.Group>

                  <Form.Group className="mb-3 position-relative">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                      type={showPasswords.new ? 'text' : 'password'}
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      placeholder="New Password"
                    />
                    <span
                      className="position-absolute"
                      onClick={() => togglePassword('new')}
                      style={{ top: '38px', right: '10px', cursor: 'pointer' }}
                    >
                      {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </Form.Group>

                  <Form.Group className="mb-4 position-relative">
                    <Form.Label>Confirm Password</Form.Label>
                    <Form.Control
                      type={showPasswords.confirm ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm Password"
                    />
                    <span
                      className="position-absolute"
                      onClick={() => togglePassword('confirm')}
                      style={{ top: '38px', right: '10px', cursor: 'pointer' }}
                    >
                      {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </Form.Group>

                  <div className="text-center">
                    <Button
                      variant="success"
                      className="update-password-profile px-4"
                      onClick={handlePasswordUpdate}
                    >
                      Change Password
                    </Button>
                  </div>
                </Form>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Container>
      </div>
    </>
  );
};

export default MyProfile;

